import { hasS3Object } from "@/lib/aws-amplify/storage";

import { Converter } from "@/components/features/_shared";
import { convertRequiredPrepMillisecondIntoRequiredPrepMillisecondBeforeDateAndHour } from "@/components/features/_shared/validation-schema/converter";

import { DUMMY_IMAGE } from "@/constants/dummy-image";

import type { ProductDayTripPlanFormModel } from "../../../../../type";
import type { ServerModel } from "../../type";

export const serverModelToInputModelConverter = (
	serverModel: ServerModel,
): ProductDayTripPlanFormModel["InputModel"] => {
	const { plan, customEmailSetting, planCustomMailSetting, basicCustomMailSetting } = serverModel;

	// 初期値が他の設定に依存する可能性がある項目は、ここで初期値を設定する
	const emailTemplateReservation = plan.emailTemplateReservation ?? customEmailSetting?.reservation;

	const { requiredPrepMillisecondBeforeDate, requiredPrepMillisecondBeforeHour } =
		convertRequiredPrepMillisecondIntoRequiredPrepMillisecondBeforeDateAndHour(plan.requiredPrepMillisecond);

	const inputModel: ProductDayTripPlanFormModel["InputModel"] = {
		hotelID: plan.hotelID,
		planID: plan.planID,

		name: plan.name,
		mainTarget: plan.mainTarget,

		keyVisuals: plan.keyVisuals,
		/**
		 * 旧版で作成される場合、ダミー画像が設定されない
		 */
		mainKeyVisual: hasS3Object(plan.mainKeyVisual) ? plan.mainKeyVisual : DUMMY_IMAGE.s3Object,

		availableDiscountIDs: plan.availableDiscountIDs,
		availableOptionIDs: plan.availableOptionIDs,
		availableRoomIDs: plan.availableRoomIDs,

		billingTypeTarget: plan.billingTypeTarget,
		billingTypeTerm: plan.billingTypeTerm,

		checkInAvailableDayOfTheWeek: plan.checkInAvailableDayOfTheWeek! ?? [],

		checkInEnd: plan.checkInEnd,
		checkInStart: plan.checkInStart,
		checkOut: plan.checkOut,

		coSleepingKidsPrice: plan.coSleepingKidsPrice,

		defaultDiscountID: plan.defaultDiscountID,

		doNotCountKidsAsStock: plan.doNotCountKidsAsStock,

		emailTemplateReservation,

		inSale: plan.inSale,
		isChangeOnlyOption: plan.isChangeOnlyOption,
		isCoSleepingKidsPriceActive: plan.isCoSleepingKidsPriceActive,
		includedMeals: plan.includedMeals,
		isDayTrip: plan.isDayTrip,
		isHiddenHotelQuestions: plan.isHiddenHotelQuestions,
		isNoAddressNeeded: plan.isNoAddressNeeded,
		isPrivate: plan.isPrivate,
		isRequest: plan.isRequest,
		isRoomPriceFree: plan.isRoomPriceFree,
		isSCConnected: plan.isSCConnected,
		isShowRoomForGuest: plan.isShowRoomForGuest,
		isUncancelable: plan.isUncancelable,
		isUnchangeable: plan.isUnchangeable,
		notSendCinMail: plan.notSendCinMail,
		roomChangeDisable: plan.roomChangeDisable,

		kidsPolicy: plan.kidsPolicy?.map(Converter.kidsPolicyItemConverter),
		cancelPolicy: plan.cancelPolicy,

		longStayParams: plan.longStayParams,

		maxDailyStockNum: plan.maxDailyStockNum,
		maxStockNum: plan.maxStockNum,

		minimumPeopleNumPerRoom: plan.minimumPeopleNumPerRoom ?? 1,
		maxPeopleNumPerRoom: plan.maxPeopleNumPerRoom,

		minRoomNum: plan.minRoomNum ?? 1,
		maxRoomNum: plan.maxRoomNum,

		onlyUseCreditPayment: plan.onlyUseCreditPayment,
		onlyUseLocalPayment: plan.onlyUseLocalPayment,

		price: plan.price,

		questions: plan.questions,

		requiredOptionIDs: plan.requiredOptionIDs,

		requiredOptionGroupIDs: plan.requiredOptionGroupIDs,

		requiredPrepMillisecond: plan.requiredPrepMillisecond,

		saleStart: plan.saleStart,
		saleEnd: plan.saleEnd || null,
		saleStartTime: plan.saleStartTime,
		saleEndTime: plan.saleEndTime || null,
		saleStartBeforeDate: plan.saleStartBeforeDate,

		saleTypeForTerm: plan.saleTypeForTerm,

		shortStayParams: plan.shortStayParams,

		staticPriceParams: plan.staticPriceParams,

		stayableDayOfTheWeek: plan.stayableDayOfTheWeek ?? [],
		isHolidayStayable: plan.isHolidayStayable,
		isHolidayCheckInAvailable: plan.isHolidayCheckInAvailable,
		stayableEnd: plan.stayableEnd || null,
		stayableStart: plan.stayableStart,

		stockTypeTarget: plan.stockTypeTarget,
		stockTypeTerm: plan.stockTypeTerm,

		// InputModel 固有のプロパティ
		requiredPrepMillisecondBeforeDate,
		requiredPrepMillisecondBeforeHour,

		// カスタムメール設定
		atCreated: {
			text:
				planCustomMailSetting?.forReservation?.atCreated?.text ??
				basicCustomMailSetting?.forReservation?.atCreated?.text ??
				"",
			override: planCustomMailSetting?.forReservation?.atCreated?.override ?? false,
		},
		atUpdated: {
			text:
				planCustomMailSetting?.forReservation?.atUpdated?.text ??
				basicCustomMailSetting?.forReservation?.atUpdated?.text ??
				"",
			override: planCustomMailSetting?.forReservation?.atUpdated?.override ?? false,
		},
		atCanceled: {
			text:
				planCustomMailSetting?.forReservation?.atCanceled?.text ??
				basicCustomMailSetting?.forReservation?.atCanceled?.text ??
				"",
			override: planCustomMailSetting?.forReservation?.atCanceled?.override ?? false,
		},
		atCheckInBefore_00days: {
			text:
				planCustomMailSetting?.forReservation?.atCheckInBefore_00days?.text ??
				basicCustomMailSetting?.forReservation?.atCheckInBefore_00days?.text ??
				"",
			shouldSendEmail: planCustomMailSetting?.forReservation?.atCheckInBefore_00days?.shouldSendEmail ?? false,
			override: planCustomMailSetting?.forReservation?.atCheckInBefore_00days?.override ?? false,
		},
		atCheckInBefore_01days: {
			text:
				planCustomMailSetting?.forReservation?.atCheckInBefore_01days?.text ??
				basicCustomMailSetting?.forReservation?.atCheckInBefore_01days?.text ??
				"",
			shouldSendEmail: planCustomMailSetting?.forReservation?.atCheckInBefore_01days?.shouldSendEmail ?? false,
			override: planCustomMailSetting?.forReservation?.atCheckInBefore_01days?.override ?? false,
		},
		atCheckInBefore_07days: {
			text:
				planCustomMailSetting?.forReservation?.atCheckInBefore_07days?.text ??
				basicCustomMailSetting?.forReservation?.atCheckInBefore_07days?.text ??
				"",
			shouldSendEmail: planCustomMailSetting?.forReservation?.atCheckInBefore_07days?.shouldSendEmail ?? false,
			override: planCustomMailSetting?.forReservation?.atCheckInBefore_07days?.override ?? false,
		},
		atCheckOutAfter_01days: {
			text:
				planCustomMailSetting?.forReservation?.atCheckOutAfter_01days?.text ??
				basicCustomMailSetting?.forReservation?.atCheckOutAfter_01days?.text ??
				"",
			shouldSendEmail: planCustomMailSetting?.forReservation?.atCheckOutAfter_01days?.shouldSendEmail ?? false,
			override: planCustomMailSetting?.forReservation?.atCheckOutAfter_01days?.override ?? false,
		},
	} satisfies ProductDayTripPlanFormModel["InputModel"];

	return inputModel;
};
